import { useState } from 'react';
import { Pencil, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useBillingAddresses,
  useDeleteBillingAddress,
  useSetDefaultBillingAddress,
} from '@/hooks/useBillingAddresses';
import { BillingAddressModal } from './BillingAddressModal';
import type { BillingAddress } from '@jobos/shared/types/billingAddress';
import type { Manager } from '@jobos/shared/types/manager';
import { formatManagerName } from '@jobos/shared';

interface BillingAddressesSectionProps {
  clientId: string;
  managers: Manager[];
}

function formatAddress(address: BillingAddress): string {
  const parts: string[] = [];
  if (address.addressLine1) parts.push(address.addressLine1);
  if (address.addressLine2) parts.push(address.addressLine2);

  const cityStateZip: string[] = [];
  if (address.city) cityStateZip.push(address.city);
  if (address.state)
    cityStateZip.push(cityStateZip.length > 0 ? `, ${address.state}` : address.state);
  if (address.zip) cityStateZip.push(` ${address.zip}`);

  if (cityStateZip.length > 0) parts.push(cityStateZip.join(''));

  return parts.join(', ');
}

export function BillingAddressesSection({
  clientId,
  managers,
}: BillingAddressesSectionProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] =
    useState<BillingAddress | null>(null);

  const { data: addresses = [] } = useBillingAddresses(clientId);
  const { mutateAsync: deleteAddress } = useDeleteBillingAddress(clientId);
  const { mutateAsync: setDefault } = useSetDefaultBillingAddress(clientId);

  const handleAdd = () => {
    setEditingAddress(null);
    setShowModal(true);
  };

  const handleEdit = (address: BillingAddress) => {
    setEditingAddress(address);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAddress(null);
  };

  const handleDelete = async (address: BillingAddress) => {
    const label = address.label || address.addressLine1 || 'this address';
    if (confirm(`Are you sure you want to delete ${label}?`)) {
      try {
        await deleteAddress(address.id);
      } catch (error) {
        console.error('Failed to delete billing address:', error);
      }
    }
  };

  const handleSetDefault = async (address: BillingAddress) => {
    try {
      await setDefault(address.id);
    } catch (error) {
      console.error('Failed to set default billing address:', error);
    }
  };

  const getManagerName = (managerId: string | null | undefined): string | null => {
    if (!managerId) return null;
    const manager = managers.find((m) => m.id === managerId);
    if (!manager) return null;
    return formatManagerName(manager.name, manager.firstName, manager.lastName);
  };

  return (
    <>
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Billing Addresses ({addresses.length})
            </h2>
            <Button size="sm" onClick={handleAdd}>
              Add Address
            </Button>
          </div>

          {addresses.length > 0 ? (
            <div className="space-y-3">
              {addresses.map((address) => {
                const managerName = getManagerName(address.managerId);
                return (
                  <div
                    key={address.id}
                    className="flex items-start justify-between p-3 border rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {address.label && (
                          <span className="font-medium text-sm">
                            {address.label}
                          </span>
                        )}
                        {address.isDefault && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatAddress(address) || 'No address entered'}
                      </div>
                      {managerName && (
                        <div className="text-xs text-gray-500">
                          Manager: {managerName}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-1 ml-4 shrink-0">
                      {!address.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefault(address)}
                          title="Set as default"
                        >
                          <Star className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(address)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(address)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No billing addresses for this client</p>
              <Button className="mt-4" size="sm" onClick={handleAdd}>
                Add First Address
              </Button>
            </div>
          )}
        </div>
      </div>

      <BillingAddressModal
        open={showModal}
        onOpenChange={handleCloseModal}
        clientId={clientId}
        managers={managers}
        editingAddress={editingAddress}
      />
    </>
  );
}
